import React from "react";
import "./ContentCard.css";

export interface ContentCardProps {
  title: string;
  description?: string;
  cover?: string | null;
  initials?: string;
  badge?: string;
  locked?: boolean;
  progressPercent?: number | null;
  meta?: string;
  actionLabel: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
  toneIndex?: number;
}

const TONES = [
  "radial-gradient(120% 120% at 20% 0%, #1f5fd0, #0a1730)",
  "radial-gradient(120% 120% at 20% 0%, #1b8f86, #07201d)",
  "radial-gradient(120% 120% at 20% 0%, #3a53c4, #0c1130)",
  "radial-gradient(120% 120% at 20% 0%, #2b7fa8, #08202c)",
];

function initialsFrom(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "BC";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const ContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  cover,
  initials,
  badge,
  locked,
  progressPercent,
  meta,
  actionLabel,
  href,
  onClick,
  external,
  toneIndex = 0,
}) => {
  const hasProgress = typeof progressPercent === "number" && progressPercent >= 0;

  const inner = (
    <>
      <div
        className="cc-thumb"
        style={cover ? undefined : { background: TONES[toneIndex % TONES.length] }}
      >
        {cover ? (
          <img src={cover} alt="" loading="lazy" />
        ) : (
          <span className="cc-glyph">{initials || initialsFrom(title)}</span>
        )}
        {badge ? <span className="cc-badge">{badge}</span> : null}
        {locked ? (
          <span className="cc-lock" aria-label="Bloqueado">
            <LockIcon />
          </span>
        ) : null}
      </div>
      <div className="cc-body">
        <h4>{title}</h4>
        {description ? <p>{description}</p> : null}
        {hasProgress ? (
          <div className="cc-prog" aria-hidden="true">
            <i style={{ width: `${Math.min(100, Math.max(0, progressPercent as number))}%` }} />
          </div>
        ) : null}
        <div className="cc-foot">
          {meta ? <span className="cc-meta">{meta}</span> : <span className="cc-meta" />}
          <span className={`cc-btn ${locked ? "ghost" : ""}`}>{actionLabel}</span>
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        className="cc"
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }
  return (
    <button className="cc" type="button" onClick={onClick}>
      {inner}
    </button>
  );
};

export default ContentCard;
